import { PaletteMode } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      logoHeight: string;
      logoWidth: string;
      appBarHeight: string;
      navBarHeight?: string;
      demoBarHeight: string;
      sidebarWidth: string;
      smSidebarWidth?: string;
      backgroundColor: string;
      darkTextPrimary?: string;
      tooltipBG?: string;
      appMaxWidth?: string;
      width?: string;
      colors: { [key: string]: string };
      waypoints: Array<string>;
    };
  }

  interface ThemeOptions {
    custom?: {
      logoHeight?: string;
      logoWidth?: string;
      appBarHeight?: string;
      navBarHeight?: string;
      demoBarHeight?: string;
      sidebarWidth?: string;
      smSidebarWidth?: string;
      backgroundColor?: string;
      darkTextPrimary?: string;
      tooltipBG?: string;
      appMaxWidth?: string;
      width?: string;
      colors: { [key: string]: string };
      waypoints: Array<string>;
  };
}
}


// Decor color objects for direct usage
export const orange = {
  Main: '#D45222',
  50: '#FFEEE0',
  100: '#FFDFC6',
  200: '#FFD0AB',
  300: '#FFB77D',
  400: '#FFA965',
  500: '#FF9540',
  600: '#F67B19',
  700: '#E76E0D',
  800: '#D45E22',
  900: '#9C2A00',
  1000: '#751104',
};

export const blue = {
  Main: '#1E88E5',
  50: '#C1E3FF',
  100: '#ACDAFF',
  200: '#96CFFE',
  300: '#82C7FF',
  400: '#62B9FF',
  500: '#42A5F5',
  600: '#399DF4',
  700: '#1565C0',
  800: '#0D47A1',
  900: '#022E73',
  1000: '#001F4F',
};

export const cyan = {
  Main: '#017E91',
  50: '#C8F8FF',
  100: '#BFE9EF',
  200: '#9FDBE3',
  300: '#7CD2DE',
  400: '#54C1D1',
  500: '#23B2C8',
  600: '#0094AB',
  700: '#006777',
  800: '#005562',
  900: '#004550',
  1000: '#002D34',
};

export const pink = {
  Main: '#7F2267',
  50: '#FFE5F9',
  100: '#F8D0EF',
  200: '#F9BFEB',
  300: '#F8A8E3',
  400: '#EE82D2',
  500: '#E36CC4',
  600: '#CD56AE',
  700: '#A33A88',
  800: '#7C1862',
  900: '#610A4A',
  1000: '#490337',
};

export const purple = {
  Main: '#3C4ABA',
  50: '#E2E5FF',
  100: '#D7DBFF',
  200: '#CFD4FE',
  300: '#BCC3F7',
  400: '#9DA8FD',
  500: '#7988F5',
  600: '#5667E7',
  700: '#2738B7',
  800: '#1A2BA2',
  900: '#0A198A',
  1000: '#000C61',
};

export const green = {
  Main: '#00695C',
  50: '#DEEDE4',
  100: '#CFECDB',
  200: '#BEE5CF',
  300: '#A9DEC0',
  400: '#84CEA4',
  500: '#60B18A',
  600: '#289675',
  700: '#147560',
  800: '#115850',
  900: '#00433A',
  1000: '#00342D',
};

export const grey = {
  Main: '#FFFFFF',
  50: '#F4F4F4',
  100: '#EFEFEF',
  200: '#DCDCDC',
  300: '#BDBDBD',
  400: '#989898',
  500: '#7C7C7C',
  600: '#656565',
  700: '#525252',
  800: '#464646',
  900: '#3D3D3D',
  1000: '#292929',
};

export const tertiary = {
  dark: '#6D3E64',
  light: '#F3E5F5',
};

export const border = {
  first: '#E2E3E4',
  second: '#CFD0D0',
  third: '#F5F5F5',
};

const Theme = createTheme({
  custom: {
    logoHeight: '32px',
    logoWidth: '150px',
    appBarHeight: '72px',
    navBarHeight: '48px',
    demoBarHeight: '52px',
    sidebarWidth: '256px',
    smSidebarWidth: '64px',
    backgroundColor: '#F4F4F4',
    darkTextPrimary: '#33739D',
    tooltipBG: '#F1F1F1',
    appMaxWidth: '960px',
    width: '100%',
    colors: {
      lightPrimaryMain: '#33739D',
      lightTextPrimary: '#1E2731',
      lightTextSecondary: '#787878',
      lightSuccessMain: '#4A7F4B',
      lightSecondaryMain: '#CAA360',
      lightErrorMain: '#CC3B3B',
      primaryMain: '#33739D',
      lightTertiaryMain: '#8E4C7D',
      backgroundColorSecondary: '#E9E9E9',
      white: '#FFFFFF',
      lightWarningMain: '#E5772C',
      lightSuccessLight: '#438A47',
      lightInfoMain: '#0288D1',
      lightWarningShades160p: '#ED6C02',
      lightErrorShades160p: '#D32F2F',
      lightSnackbarBg: '#323232',
    },
    // Rotating colors for waypoint tracking
    waypoints: [
      blue[100],
      green[300],
      purple[300],
      orange[300],
      pink[300],
      cyan[300],
      green[500],
    ],
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 360,
      md: 720,
      lg: 960,
      xl: 1920,
    },
  },
  palette: {
    mode: 'light' as PaletteMode,
    primary: {
      main: '#33739D',
    },
    secondary: {
      main: '#CAA360',
    },
    error: {
      main: '#CC3B3B',
    },
    success: {
      main: '#4A7F4B',
    },
    warning: {
      main: '#E5772C',
    },
    info: {
      main: '#0288D1',
    },
    text: {
      primary: '#1E2731',
      secondary: '#787878',
      disabled: '#999A9E',
    },
  },
  typography: {
    htmlFontSize: 16,
    fontSize: 16,
    fontFamily: "'Roboto', sans-serif",
    h1: {
      fontSize: '2.25rem', // 36px / 16 = 2.25rem
      fontWeight: 600,
      lineHeight: 1.2,
      [createTheme().breakpoints.down('md')]: {
        fontSize: '2rem', // 32px for mobile
      },
    },
    h2: {
      fontSize: '1.75rem', // 28px / 16 = 1.75rem
      fontWeight: 600,
      lineHeight: 1.2,
      [createTheme().breakpoints.down('md')]: {
        fontSize: '1.5rem', // 24px for mobile
      },
    },
    h3: {
      fontSize: '1.5rem', // 24px / 16 = 1.5rem
      fontWeight: 600,
      lineHeight: 1.167,
      [createTheme().breakpoints.down('md')]: {
        fontSize: '1.25rem', // 20px for mobile
      },
    },
    h4: {
      fontSize: '1.5rem', // 24px / 16 = 1.5rem
      fontWeight: 400,
      lineHeight: 1.235,
      [createTheme().breakpoints.down('md')]: {
        fontSize: '1.125rem', // 18px for mobile
      },
    },
    h5: {
      fontSize: '1.25rem', // 20px / 16 = 1.25rem
      fontWeight: 600,
      lineHeight: 1.2,
      [createTheme().breakpoints.down('md')]: {
        fontSize: '1rem', // 16px for mobile
      },
    },
    body1: {
      fontSize: '1rem', // 16px / 16 = 1rem
      fontWeight: 400,
      lineHeight: 1.5,
      [createTheme().breakpoints.down('md')]: {
        fontSize: '0.875rem', // 14px for mobile
      },
    },
    body2: {
      fontSize: '0.875rem', // 14px / 16 = 0.875rem
      fontWeight: 400,
      lineHeight: 1.43,
      [createTheme().breakpoints.down('md')]: {
        fontSize: '0.75rem', // 12px for mobile
      },
    },
    subtitle1: {
      fontSize: '1rem', // 16px / 16 = 1rem
      fontWeight: 600,
      lineHeight: 1.5,
      color: blueGrey[200],
      [createTheme().breakpoints.down('md')]: {
        fontSize: '0.875rem', // 14px for mobile
      },
    },
    subtitle2: {
      fontSize: '0.875rem', // 14px / 16 = 0.875rem
      fontWeight: 600,
      lineHeight: 1.43,
      [createTheme().breakpoints.down('md')]: {
        fontSize: '0.75rem', // 12px for mobile
      },
    },
    caption: {
      fontSize: '0.75rem', // 12px / 16 = 0.75rem
      fontWeight: 400,
      lineHeight: 1.66,
      [createTheme().breakpoints.down('md')]: {
        fontSize: '0.625rem', // 10px for mobile
      },
    },
    button: {
      fontSize: '0.875rem', // 14px / 16 = 0.875rem
      fontWeight: 500,
      borderRadius: '3px',
      padding: '6px 12px',
      textTransform: 'none',
      [createTheme().breakpoints.down('md')]: {
        fontSize: '0.75rem', // 12px for mobile
      },
    },
  },
  components: {
    MuiTextField: { defaultProps: { autoComplete: 'off' } },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          display: 'none',
        },
      },
    },
  },
});

export default Theme;
